
import React, { useState, useEffect, useRef, Suspense, lazy, useMemo } from 'react';
import { 
  Routes, 
  Route, 
  Navigate, 
  useNavigate, 
  useLocation,
  useParams,
  Link
} from 'react-router-dom';
import { BrowserRouter as Router } from 'react-router-dom';
import { AuthProvider, AuthGuard, useAuth } from './components/AuthBridge';
import { cleanSearchParams } from './services/authSanitizer';
import { Sidebar } from './components/Sidebar';
import { Dashboard } from './pages/Dashboard';
import { PracticeExam } from './pages/PracticeExam';
import { ExamHistory } from './pages/ExamHistory';
import { AITutor } from './pages/AITutor';
import { StudyQuiz } from './pages/StudyQuiz';
import { StudyGuide } from './pages/StudyGuide';
import { SkillsLab } from './pages/SkillsLab';
import { SimulatorLab } from './pages/SimulatorLab';
import { Podcasts } from './pages/Podcasts';
import { Profile } from './pages/Profile';
import { HelpPage } from './pages/HelpPage';
import { RegistrySearch } from './pages/RegistrySearch';
import { ScenarioPicker } from './pages/ScenarioPicker';
import { NotFoundPage } from './pages/NotFoundPage'; 
import { CookieConsentPage } from './pages/CookieConsentPage'; 
import { EliteUpgrade } from './pages/EliteUpgrade'; 
import { MaintenancePage } from './pages/MaintenancePage'; 
import { Flashcards } from './pages/Flashcards';
import { AdvancedAnalytics } from './pages/AdvancedAnalytics';
import { 
  Shield, Zap, Loader2, Menu as MenuIcon, Sparkles, 
  UserCircle, HelpCircle, LogOut, ChevronDown, Activity, 
  ShieldCheck, ArrowRight, Search as SearchIcon, Eye, EyeOff, Database,
  Globe, Sun, Moon, AlertCircle, X, CheckCircle, CheckCircle2
} from 'lucide-react';
import { AdOverlay } from './components/AdOverlay';
import { PromoBanner } from './components/PromoBanner'; 
import { SponsoredService } from './services/sponsoredService';
import { AdminPermission, StudyStats } from './types';
import { StorageService } from './services/storageService';
import { AudioProvider } from './components/AudioContext';
import { GlobalAudioPlayer } from './components/GlobalAudioPlayer';
import { MiniPlayer } from './components/MiniPlayer';
import { NotificationCenter } from './components/NotificationCenter';
import { AnalyticsTracker } from './components/AnalyticsTracker';
import { Footer } from './components/Footer';
import { SEO } from './components/SEO';
import { CookieConsent } from './components/CookieConsent';
import { Breadcrumbs } from './components/Breadcrumbs';
import { useThemeTokens } from './hooks/useActiveTheme';
import { PageLoader } from './components/PageLoader';
import { ToastNotificationContainer } from './components/ToastNotification';
import { NetworkStatusIndicator } from './components/NetworkStatusIndicator';

const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const CertificationManager = lazy(() => import('./pages/admin/CertificationManager').then(m => ({ default: m.CertificationManager })));
const AdminAIFactory = lazy(() => import('./pages/admin/AdminAIFactory').then(m => ({ default: m.AdminAIFactory })));
const PromptConsole = lazy(() => import('./pages/admin/PromptConsole').then(m => ({ default: m.PromptConsole })));
const QuestionManager = lazy(() => import('./pages/admin/QuestionManager').then(m => ({ default: m.QuestionManager })));
const UserManager = lazy(() => import('./pages/admin/UserManager').then(m => ({ default: m.UserManager })));
const ExamConfigManager = lazy(() => import('./pages/admin/ExamConfigManager').then(m => ({ default: m.ExamConfigManager })));
const AdManager = lazy(() => import('./pages/admin/AdManager').then(m => ({ default: m.AdManager })));
const PromoManager = lazy(() => import('./pages/admin/PromoManager').then(m => ({ default: m.PromoManager }))); 
const StudyContentManager = lazy(() => import('./pages/admin/StudyContentManager').then(m => ({ default: m.StudyContentManager })));
const SkillsContentManager = lazy(() => import('./pages/admin/SkillsContentManager').then(m => ({ default: m.SkillsContentManager })));
const ClinicalSimulatorStudio = lazy(() => import('./pages/admin/ClinicalSimulatorStudio').then(m => ({ default: m.ClinicalSimulatorStudio })));
const SimulatorManager = lazy(() => import('./pages/admin/SimulatorManager').then(m => ({ default: m.SimulatorManager })));
const PodcastManager = lazy(() => import('./pages/admin/PodcastManager').then(m => ({ default: m.PodcastManager })));
const AiStatusManager = lazy(() => import('./pages/admin/AiStatusManager').then(m => ({ default: m.AiStatusManager })));
const ScenarioManager = lazy(() => import('./pages/admin/ScenarioManager').then(m => ({ default: m.ScenarioManager })));
const NotificationManager = lazy(() => import('./pages/admin/NotificationManager').then(m => ({ default: m.NotificationManager })));
const DatabaseManager = lazy(() => import('./pages/admin/DatabaseManager').then(m => ({ default: m.DatabaseManager })));
const SeoManager = lazy(() => import('./pages/admin/SeoManager').then(m => ({ default: m.SeoManager })));
const FlashcardManager = lazy(() => import('./pages/admin/FlashcardManager').then(m => ({ default: m.FlashcardManager })));
const FeedbackManager = lazy(() => import('./pages/admin/FeedbackManager').then(m => ({ default: m.FeedbackManager })));
const Faculty = lazy(() => import('./pages/Faculty').then(m => ({ default: m.Faculty })));
const FacultyManager = lazy(() => import('./pages/admin/FacultyManager').then(m => ({ default: m.FacultyManager })));
const SuperAdminAnalytics = lazy(() => import('./pages/admin/SuperAdminAnalytics').then(m => ({ default: m.SuperAdminAnalytics })));
const UserJourneyAuditor = lazy(() => import('./pages/admin/UserJourneyAuditor').then(m => ({ default: m.UserJourneyAuditor })));
const CertAuditor = lazy(() => import('./pages/admin/CertAuditor').then(m => ({ default: m.CertAuditor })));

const FaqPage = lazy(() => import('./pages/FaqPage').then(m => ({ default: m.FaqPage })));
const FaqManager = lazy(() => import('./pages/admin/FaqManager').then(m => ({ default: m.FaqManager })));
const ProgramDetailsPage = lazy(() => import('./pages/ProgramDetailsPage').then(m => ({ default: m.ProgramDetailsPage })));

declare global {
  interface Window {
    deferredPWAInstallPrompt: any;
    resetDiagnostic: () => Promise<void>;
  }
}

const PermissionGuard: React.FC<{ children: React.ReactNode; permission: AdminPermission }> = ({ children, permission }) => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const hasAccess = useMemo(() => {
    if (!user) return false;
    const isAnyAdmin = user.role === 'admin' || (user as any).role === 'superadmin' || (user as any).role === 'super_admin';
    if (!isAnyAdmin) return false;
    if (!user.permissions || user.permissions.length === 0 || user.permissions.includes('*') || user.permissions.includes(permission)) {
      return true;
    }
    return false;
  }, [user, permission]);
  useEffect(() => {
    if (!isLoading && user && !hasAccess) {
      navigate(`/practice/${user.activeCertId || 'cna'}/dashboard`, { replace: true });
    }
  }, [user, hasAccess, navigate, isLoading]);
  if (isLoading) return <PageLoader fullScreen text="Verifying Permissions..." />;
  if (!hasAccess) return null;
  return <Suspense fallback={<PageLoader fullScreen text="Loading View..." />}>{children}</Suspense>;
};

const AdminDirectRedirect: React.FC<{ defaultSubpage?: string }> = ({ defaultSubpage }) => {
  const { subpage } = useParams<{ subpage?: string }>();
  const location = useLocation();
  const target = defaultSubpage || subpage || 'dashboard';
  return <Navigate to={`/manage/admin-${target}${location.search}`} replace />;
};

const GatewayRedirect: React.FC = () => {
  useEffect(() => {
    window.location.href = '/gateway';
  }, []);
  return <PageLoader fullScreen text="Loading Study Hub Gateway..." />;
};

const CertSyncHandler = () => {
  const { certId } = useParams<{ certId: string }>();
  const { activeCert, switchCert, isLoading } = useAuth();
  const navigate = useNavigate();
  const [isVerifying, setIsVerifying] = useState(false);
  const switchingTo = useRef<string | null>(null);

  useEffect(() => {
    if (!isLoading && certId) {
      const targetId = certId.toLowerCase();
      if (targetId === 'manage' || targetId === '404' || targetId === 'faq' || targetId === 'program' || targetId === 'programs' || targetId === 'certifications' || targetId === 'practice-exams') return;
      
      if (activeCert && activeCert.id === targetId) {
        switchingTo.current = null;
        return;
      }

      if (switchingTo.current === targetId) return;
      switchingTo.current = targetId;

      setIsVerifying(true);
      StorageService.getCertifications().then(certs => {
        const match = certs.find(c => c.id === targetId);
        if (!match) {
          navigate('/404', { replace: true });
        } else {
          return switchCert(targetId);
        }
      }).catch(err => {
        console.error("Registry Verification Protocol Failure", err);
        switchingTo.current = null;
      }).finally(() => {
        setIsVerifying(false);
      });
    }
  }, [certId, activeCert, switchCert, isLoading, navigate]);

  if (isVerifying && !activeCert) return <PageLoader fullScreen text="Synchronizing Certification Registry..." />;
  return null;
};

const NavigateWithCert = ({ target }: { target: string }) => {
  const { user, activeCert } = useAuth();
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const mode = search.get('mode');
  
  const certId = activeCert?.id || user?.activeCertId || 'cna';
  
  let finalTarget = target;
  if (target === 'practice' && mode === 'missed') {
    finalTarget = 'missed';
  }
  
  const cleanSearch = cleanSearchParams(location.search);
  return <Navigate to={`/practice/${certId}/${finalTarget}${cleanSearch}`} replace />;
};

const RedirectToPractice = () => {
  const { certId } = useParams<{ certId: string }>();
  const location = useLocation();
  const pathParts = location.pathname.split('/').filter(Boolean);
  
  // Guard against self-redirect loops: if already on a /practice path, do not redirect
  if (pathParts[0] === 'practice') {
    return <Navigate to="/404" replace />;
  }

  const cIndex = pathParts.findIndex(p => p.toLowerCase() === (certId || '').toLowerCase());
  const subPath = cIndex !== -1 ? pathParts.slice(cIndex + 1).join('/') : 'dashboard';
  const cleanSearch = cleanSearchParams(location.search);
  return <Navigate to={`/practice/${certId || 'cna'}/${subPath || 'dashboard'}${cleanSearch}`} replace />;
};

const AppLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, isLoading, activeCert, updateUser, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [adminMode, setAdminMode] = useState(true); 
  const lastOverlayTime = useRef<number>(Date.now());
  const [adConfig, setAdConfig] = useState(SponsoredService.getConfig());
  const [hasConfigFetched, setHasConfigFetched] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Global Exit-Intent Survey State
  const [showExitSurvey, setShowExitSurvey] = useState(false);

  // Backward-compatibility and clean URL hash-to-path migration
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hash) {
      const hash = window.location.hash;
      if (hash.startsWith('#/programs/')) {
        const id = hash.replace('#/programs/', '').trim();
        navigate(`/programs/${id}`, { replace: true });
      } else if (hash.startsWith('#/certifications/')) {
        const id = hash.replace('#/certifications/', '').trim();
        navigate(`/programs/${id}`, { replace: true });
      } else if (hash.startsWith('#/')) {
        const target = hash.slice(1);
        navigate(target, { replace: true });
      }
    }
  }, [navigate]);
  // Cancel any active speech synthesis on global route changes
  useEffect(() => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  }, [location.pathname]);

  const [surveyFormat, setSurveyFormat] = useState<'struggle' | 'wishlist'>('struggle');
  const [selectedStruggleReason, setSelectedStruggleReason] = useState<string>('');
  const [customStruggleMessage, setCustomStruggleMessage] = useState<string>('');
  const [isSubmittingSurvey, setIsSubmittingSurvey] = useState(false);
  const [surveySubmittedSuccessfully, setSurveySubmittedSuccessfully] = useState(false);
  const [surveySubmitError, setSurveySubmitError] = useState<string | null>(null);
  const [cooldownChangeTrigger, setCooldownChangeTrigger] = useState(0);

  const isUserPage = useMemo(() => {
    return !location.pathname.startsWith('/manage') && user?.role !== 'admin';
  }, [location.pathname, user?.role]);

  const isSurveyEligible = useMemo(() => {
    if (!user || !isUserPage) return false;
    const completedAlready = localStorage.getItem('completed_exit_intent_survey') === 'true';
    if (completedAlready) return false;

    const cooldownUntilStr = localStorage.getItem('exit_intent_survey_cooldown_until');
    if (cooldownUntilStr) {
      const cooldownUntil = parseInt(cooldownUntilStr, 10);
      if (Date.now() < cooldownUntil) {
        return false;
      }
    }
    return true;
  }, [user, isUserPage, cooldownChangeTrigger]);

  const isSurveyUserNewOrNonActive = useMemo(() => {
    if (!user) return false;
    if (adConfig?.onboardingFeatureEnabled === false) return false;

    const thresholdHours = adConfig?.onboardingHoursThreshold ?? 48;
    const registrationTime = user.createdAt ? new Date(user.createdAt).getTime() : Date.now();
    const hoursElapsed = (Date.now() - registrationTime) / (1000 * 60 * 60);
    const isNewUser = hoursElapsed <= thresholdHours;

    const hasNoQuizzes = !user?.stats?.quizzesTaken || user.stats.quizzesTaken === 0;
    const hasEmptyHistory = !user?.history || user.history.length === 0;

    return isNewUser && (hasNoQuizzes || hasEmptyHistory);
  }, [user, adConfig?.onboardingFeatureEnabled, adConfig?.onboardingHoursThreshold]);

  // Hook into mouseleave, beforeunload, and mobile pull-down touch triggers for Exit-Intent feedback
  useEffect(() => {
    if (!isSurveyEligible) return;

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY < 15 && !showExitSurvey) {
        let chosenFormat: 'struggle' | 'wishlist' = 'struggle';
        if (!isSurveyUserNewOrNonActive) {
          chosenFormat = Math.random() < 0.5 ? 'struggle' : 'wishlist';
        }
        setSurveyFormat(chosenFormat);
        setShowExitSurvey(true);
        if ((window as any).trackEvent) {
          (window as any).trackEvent('exit_intent_survey_triggered', 'survey', `${location.pathname}?format=${chosenFormat}`);
        }
      }
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      let chosenFormat: 'struggle' | 'wishlist' = 'struggle';
      if (!isSurveyUserNewOrNonActive) {
        chosenFormat = Math.random() < 0.5 ? 'struggle' : 'wishlist';
      }
      setSurveyFormat(chosenFormat);
      setShowExitSurvey(true);
      const confirmationMessage = "Before you leave, please share your quick feedback so we can improve the study experience!";
      e.preventDefault();
      e.returnValue = confirmationMessage;
      return confirmationMessage;
    };

    let touchStartY = 0;
    let isTrackingTouch = false;

    const handleTouchStart = (e: TouchEvent) => {
      if (showExitSurvey) return;
      if (e.touches && e.touches.length > 0) {
        const clientY = e.touches[0].clientY;
        // Target pulls initiating from near the top edge (within 80px)
        if (clientY < 80) {
          touchStartY = clientY;
          isTrackingTouch = true;
        }
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isTrackingTouch || showExitSurvey) return;
      if (e.touches && e.touches.length > 0) {
        const currentY = e.touches[0].clientY;
        const deltaY = currentY - touchStartY;
        // Touch gesture pulled downwards over 120px from top
        if (deltaY > 120) {
          isTrackingTouch = false;
          let chosenFormat: 'struggle' | 'wishlist' = 'struggle';
          if (!isSurveyUserNewOrNonActive) {
            chosenFormat = Math.random() < 0.5 ? 'struggle' : 'wishlist';
          }
          setSurveyFormat(chosenFormat);
          setShowExitSurvey(true);
          if ((window as any).trackEvent) {
            (window as any).trackEvent('exit_intent_survey_triggered_mobile', 'survey', `${location.pathname}?format=${chosenFormat}`);
          }
        }
      }
    };

    const handleTouchEnd = () => {
      isTrackingTouch = false;
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    window.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [isSurveyEligible, isSurveyUserNewOrNonActive, showExitSurvey, location.pathname]);

  // Expose global developer console command to manually force trigger the Exit-Intent survey modal
  useEffect(() => {
    (window as any).triggerExitSurvey = (format?: 'struggle' | 'wishlist') => {
      console.log("%c[Dev Tools] Manually forcing Exit-Intent Survey display state.", "color: #f43f5e; font-weight: bold;");
      let chosen = format;
      if (!chosen) {
        chosen = isSurveyUserNewOrNonActive ? 'struggle' : (Math.random() < 0.5 ? 'struggle' : 'wishlist');
      }
      setSurveyFormat(chosen);
      setShowExitSurvey(true);
    };
    return () => {
      try {
        delete (window as any).triggerExitSurvey;
      } catch (e) {}
    };
  }, [isSurveyUserNewOrNonActive]);

  const handleSubmitExitSurvey = async () => {
    if (!selectedStruggleReason) {
      setSurveySubmitError(surveyFormat === 'struggle' 
        ? "Please select a struggle reason to submit feedback." 
        : "Please select a feature from the wishlist to submit feedback."
      );
      return;
    }
    
    setIsSubmittingSurvey(true);
    setSurveySubmitError(null);
    try {
      let categoryMsg = "";
      const categoryType = 'Exit Survey';

      if (surveyFormat === 'struggle') {
        const reasonLabelMap: Record<string, string> = {
          'difficult_content': 'The words and lessons are too hard to understand',
          'confusing_ui': 'The app is hard to use or navigate',
          'missing_info': 'I cannot find the topic I want to study',
          'leaving_now': 'Just looking around, I will come back later',
          'other': 'Something else'
        };
        categoryMsg = `Struggle: ${reasonLabelMap[selectedStruggleReason] || selectedStruggleReason}. Details: ${customStruggleMessage || 'No extra comments provided.'}`;
      } else {
        const wishlistLabelMap: Record<string, string> = {
          'flashcards': 'Ready-to-use study flashcards & fun learning games',
          'study_groups': 'Chat rooms to talk and study with other students',
          'video_skills': 'Short videos showing how to perform hands-on skills (like washing hands, taking pulse)',
          'custom_mock': 'Unlimited custom practice exams',
          'mobile_app': 'A mobile app you can download on your phone'
        };
        categoryMsg = `Wishlist Selection: ${wishlistLabelMap[selectedStruggleReason] || selectedStruggleReason}. Details: ${customStruggleMessage || 'No extra comments provided.'}`;
      }

      const currentCertId = activeCert?.id || user?.activeCertId || 'cna';

      await StorageService.submitFeedback({
        category: categoryType,
        message: categoryMsg,
        state: user?.state || 'General',
        certId: currentCertId,
        metadata: {
          userAgent: navigator.userAgent,
          platform: navigator.platform,
          selectedReason: selectedStruggleReason,
          activePage: location.pathname,
          variant: surveyFormat
        }
      });
      
      await StorageService.trackUserEvent({
        action: 'exit_intent_survey_submitted',
        category: 'survey',
        label: `${surveyFormat}:${selectedStruggleReason}`
      });
      
      setSurveySubmittedSuccessfully(true);
      localStorage.setItem('completed_exit_intent_survey', 'true');
      sessionStorage.setItem('first_session_survey_eligible', 'false');
      
      setTimeout(() => {
        setShowExitSurvey(false);
        setSurveySubmittedSuccessfully(false);
        setSelectedStruggleReason('');
        setCustomStruggleMessage('');
        setSurveySubmitError(null);
      }, 2500);
      
    } catch (e) {
      console.error("Failed to submit exit intent feedback:", e);
      setSurveySubmitError("Feedback database momentarily offline. Resume study anytime!");
    } finally {
      setIsSubmittingSurvey(false);
    }
  };

  const handleCancelExitSurvey = () => {
    setShowExitSurvey(false);
    setSelectedStruggleReason('');
    setCustomStruggleMessage('');
    setSurveySubmitError(null);
    try {
      localStorage.setItem('exit_intent_survey_cooldown_until', (Date.now() + 60 * 60 * 1000).toString());
      setCooldownChangeTrigger(prev => prev + 1);
    } catch (e) {
      console.error("Local storage lookup during cooldown set failure", e);
    }
    if ((window as any).trackEvent) {
      (window as any).trackEvent('exit_intent_survey_resume', 'survey', `${location.pathname}?format=${surveyFormat}`);
    }
  };

  // Automatically scroll viewport to top when swapping pages/routes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    try {
      const persisted = localStorage.getItem('theme_preference');
      if (persisted === 'dark') return 'dark';
      if (persisted === 'light') return 'light';
      // Strictly default to Light Mode
      return 'light';
    } catch {
      return 'light';
    }
  });

  useEffect(() => {
    try {
      if (theme === 'dark') {
        document.documentElement.classList.add('dark');
        localStorage.setItem('theme_preference', 'dark');
      } else {
        document.documentElement.classList.remove('dark');
        localStorage.setItem('theme_preference', 'light');
      }
      window.dispatchEvent(new CustomEvent('theme-preference-updated', { detail: theme }));
    } catch (e) {
      console.warn('LocalStorage error syncing theme preferred direction:', e);
    }
  }, [theme]);

  useEffect(() => {
    window.resetDiagnostic = async () => {
      try {
        localStorage.removeItem('elite_diagnostic_completed');
        await updateUser({ hasCompletedDiagnostic: false });
        console.log("[DEV] Diagnostic status reset successfully.");
        alert("Diagnostic status reset. Refresh the page to re-trigger onboarding.");
      } catch (err) {
        console.error("Failed to reset diagnostic status:", err);
      }
    };
  }, [updateUser]);

  useEffect(() => {
    const handlePwaRegistration = async () => {
        if ('serviceWorker' in navigator) {
            if (adConfig.isPwaEnabled) {
                try {
                    // Register service worker with root scope for full app coverage
                    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
                    console.log("[PWA] Service Worker Operational with scope:", registration.scope);
                } catch (e) {
                    // Fallback to relative path if root register fails
                    try {
                        const fallbackReg = await navigator.serviceWorker.register('/practice-exams/sw.js', { scope: '/practice-exams/' });
                        console.log("[PWA] Service Worker Operational (fallback):", fallbackReg.scope);
                    } catch (fallbackErr) {
                        console.warn("[PWA] Registration Interrupted", fallbackErr);
                    }
                }
            } else {
                const registrations = await navigator.serviceWorker.getRegistrations();
                for (const reg of registrations) {
                    await reg.unregister();
                }
                console.log("[PWA] Service Worker De-registered by Admin");
            }
        }
    };
    handlePwaRegistration();

    const handleInstallPrompt = (e: any) => {
        e.preventDefault();
        window.deferredPWAInstallPrompt = e;
        window.dispatchEvent(new CustomEvent('pwa-install-available'));
    };

    const handleAppInstalled = () => {
        console.log("[PWA] Application successfully installed to device");
        window.deferredPWAInstallPrompt = null;
        window.dispatchEvent(new CustomEvent('pwa-installed'));
    };

    window.addEventListener('beforeinstallprompt', handleInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    return () => {
        window.removeEventListener('beforeinstallprompt', handleInstallPrompt);
        window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [adConfig.isPwaEnabled]);

  useEffect(() => {
    SponsoredService.fetchLatestConfig().finally(() => {
      setHasConfigFetched(true);
    });
    const handleUpdate = () => setAdConfig(SponsoredService.getConfig());
    window.addEventListener('sponsored-config-updated', handleUpdate);
    return () => window.removeEventListener('sponsored-config-updated', handleUpdate);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
            setProfileMenuOpen(false);
        }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const pathParts = location.pathname.split('/').filter(Boolean);
  const isFaqRoute = pathParts[0] === 'faq';
  const isPracticeRoute = pathParts[0] === 'practice' || pathParts[0] === 'practice-exams';
  const currentPage = isFaqRoute ? 'faq' : (isPracticeRoute ? (pathParts[2] || 'dashboard') : (pathParts[1] || 'dashboard'));

  const seoTitle = useMemo(() => {
    const certName = activeCert?.shortName || 'Clinical';
    switch(currentPage) {
        case 'faq': return `Frequently Asked Questions | Diamond Prep Hub`;
        case 'admin-faqs': return `FAQ Control Platform | Admin Administered`;
        case 'dashboard': return `${certName} Study Dashboard`;
        case 'analytics': return `${certName} Advanced Analytics Pro`;
        case 'guide': return `${certName} Theoretical Study Guide`;
        case 'flashcards': return `${certName} Interactive Flashcards`;
        case 'skills': return `${certName} Procedural Skills Laboratory`;
        case 'simulator': return `${certName} Clinical Math Laboratory`;
        case 'podcasts': return `${certName} Expert Audio Lessons`;
        case 'exam': return `Official ${certName} Board Simulation`;
        case 'scenario': return `${certName} Situational Case Studies`;
        case 'search': return `Registry AI Search Bridge`;
        case 'profile': return `Candidate Command Center`;
        case 'elite': return `Elite Pro Registration`;
        case 'help': return `Study Hub Operational Manual`;
        default: return null;
    }
  }, [currentPage, activeCert]);

  const isElite = user?.planTier === 'paid';
  const isAdmin = user?.role === 'admin';
  const showSidebar = isAuthenticated;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [location.pathname]);

  if (adConfig.isMaintenanceMode && user?.role !== 'admin') {
      return <MaintenancePage title={adConfig.maintenanceTitle} message={adConfig.maintenanceMessage} />;
  }

  useEffect(() => {
    if (isAuthenticated && user && !isLoading) {
        const isManageParam = typeof window !== 'undefined' && (
          window.location.href.includes('role=manage') || 
          window.location.search.includes('role=manage') ||
          window.location.hash.includes('role=manage')
        );

        if (location.pathname === '/' || location.pathname === '/manage' || (user.role === 'admin' && isManageParam)) {
            const targetCert = user.activeCertId || 'cna';
            if (user.role === 'admin') navigate('/manage/admin-dashboard', { replace: true });
            else navigate(`/practice/${targetCert}/dashboard`, { replace: true });
        }
    }
  }, [isAuthenticated, user, isLoading, location.pathname, navigate]);

  useEffect(() => {
    if (!isAuthenticated || !user || user.role === 'admin') return;

    const interval = setInterval(async () => {
      const currentStats: StudyStats = user.stats || {
        quizzesTaken: 0,
        averageScore: 0,
        studyHours: 0,
        topicsMastered: 0,
        points: 0,
        level: 1,
        badges: []
      };
      const increment = 1 / 60; 
      const newHours = (currentStats.studyHours || 0) + increment;
      
      try {
        await updateUser({ 
          stats: { 
            ...currentStats, 
            studyHours: Number(newHours.toFixed(4)) 
          } 
        });

        if (user.planTier === 'free' && adConfig.isActive && adConfig.overlaySettings.enabled) {
            const timeSinceLast = (Date.now() - lastOverlayTime.current) / 1000 / 60;
            if (timeSinceLast >= adConfig.overlaySettings.triggerMinutes && !showOverlay) {
                setShowOverlay(true);
                lastOverlayTime.current = Date.now();
            }
        }
      } catch (err) {
        console.error("Failed to sync study time heartbeat:", err);
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [isAuthenticated, user?.id, user?.role, user?.planTier, updateUser, showOverlay, adConfig]);

  const handleNavigate = (page: string) => {
    const cert = activeCert?.id || user?.activeCertId || 'cna';
    if (page.startsWith('admin-')) {
      navigate(`/manage/${page}`);
    } else if (page === 'program' || page === 'programs' || page === 'overview' || page === 'guide-faq') {
      navigate(`/programs/${cert}`);
    } else {
      navigate(`/practice/${cert}/${page}`);
    }
    setProfileMenuOpen(false);
  };

  const isFixed = adConfig.headerMode === 'fixed';
  const headerClass = isFixed 
    ? (showSidebar ? 'fixed top-0 left-0 lg:left-64 right-0 z-[100]' : 'fixed top-0 left-0 right-0 z-[100]') 
    : 'relative';
  const mainWrapperClass = isFixed ? 'pt-16 md:pt-20' : '';
  const xpPercent = Math.min(100, ((user?.stats?.xpToday || 0) / (user?.stats?.dailyXPGoal || 500)) * 100);

  const tokens = useThemeTokens();
  const isClinicalTheme = tokens.isClinical;

  return (
    <div className="relative min-h-screen w-full flex bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 transition-colors duration-200 overflow-x-hidden">
      <AnalyticsTracker />
      <CookieConsent />
      <SEO title={seoTitle || undefined} />
      <ToastNotificationContainer />
      {showOverlay && <AdOverlay onClose={() => setShowOverlay(false)} />}
      {showSidebar && <Sidebar currentPage={currentPage} onNavigate={handleNavigate} onLogout={logout} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} adminMode={isAdmin && adminMode} />}
      
      <div className={`flex-1 flex flex-col min-w-0 min-h-screen ${showSidebar ? 'lg:pl-64' : 'lg:pl-0'} transition-all duration-300 relative isolate`}>
        <header className={`${headerClass} h-16 md:h-20 ${
          (!isAuthenticated || tokens.isClinical) 
            ? 'bg-[#0B2942] border-b border-slate-800 text-white' 
            : tokens.isHighContrast 
            ? 'bg-black text-amber-400 border-b-2 border-black'
            : tokens.isMinimal 
            ? 'bg-slate-900 text-white border-b border-slate-800'
            : tokens.isVibrant 
            ? 'bg-gradient-to-r from-violet-950 via-indigo-950 to-slate-950 text-white border-b border-violet-800'
            : 'bg-white/80 dark:bg-slate-900/80 border-b border-slate-200 dark:border-slate-800'
        } backdrop-blur-md px-4 md:px-8 flex items-center justify-between shrink-0 shadow-sm transition-colors duration-200`} role="banner">
          <div 
            className="absolute bottom-0 left-0 w-full h-[1px] bg-slate-100/50"
            role="progressbar"
            aria-valuenow={xpPercent}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Daily XP Goal Progress"
          >
            <div 
              className={`h-full transition-all duration-1000 ease-out ${tokens.progressBar}`} 
              style={{ width: `${xpPercent}%` }}
            />
          </div>

          {showSidebar && (
            <button 
              onClick={() => setSidebarOpen(true)} 
              className={`lg:hidden p-2 ${(!isAuthenticated || isClinicalTheme) ? 'text-slate-200' : 'text-slate-500'}`}
              aria-label="Open Sidebar Menu"
              aria-expanded={sidebarOpen}
            >
              <MenuIcon size={22} />
            </button>
          )}
          
          <div className="flex items-center gap-2 md:gap-4 flex-1 justify-start overflow-hidden">
            <a 
              href="/gateway" 
              className={`flex items-center gap-2 text-white hover:opacity-90 transition-opacity shrink-0 ${showSidebar ? 'lg:hidden' : ''}`} 
              style={{ textDecoration: 'none' }}
              aria-label="Diamond School of Nursing Study Hub"
            >
              <img 
                src={adConfig.schoolLogoUrl || 'https://static.wixstatic.com/shapes/cc4206_330c2df5c9be45088f9d7ba5b00d2a6d.svg'} 
                alt="Diamond School of Nursing" 
                className="h-7 sm:h-8 md:h-9 w-auto object-contain max-w-[150px] sm:max-w-[200px] bg-transparent shrink-0"
              />
            </a>

            {(!isAuthenticated || isClinicalTheme) && !(isAdmin && adminMode) && (
              <div className="hidden lg:flex items-center gap-1.5 px-3 py-1 bg-[#0F7D6C]/25 text-teal-300 rounded-full text-[10px] font-bold border border-[#0F7D6C]/50 shrink-0">
                <CheckCircle size={12} className="text-teal-400 shrink-0" />
                <span>State Board Handbook Aligned</span>
              </div>
            )}
            
            {isElite && (
                <div className={`flex items-center gap-2 transition-all ${adConfig.headerSlimMobile ? 'hidden sm:flex' : 'flex'} px-5 py-1.5 bg-gradient-to-r from-amber-400 via-yellow-200 to-amber-600 rounded-full shadow-[0_5px_15px_rgba(245,158,11,0.2)] border border-amber-300/30`}>
                    <Sparkles aria-hidden="true" size={12} className="text-slate-950 fill-slate-950" />
                    <span className="text-[10px] font-black text-slate-950 uppercase tracking-[0.25em]">Elite Pro</span>
                </div>
            )}

            {isElite && adConfig.isRegistrySearchEnabled && (
               <button 
                onClick={() => handleNavigate('search')}
                className="p-2 text-slate-400 hover:text-indigo-600 transition-all hidden md:block"
                aria-label="Search Registry"
               >
                 <SearchIcon size={18} />
               </button>
            )}

            {isAdmin && (
              <button 
                onClick={() => setAdminMode(!adminMode)}
                className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all border border-slate-200 group"
                title={adminMode ? "Switch to Student View" : "Return to Admin View"}
                aria-label={adminMode ? "Switch to Student View" : "Return to Admin View"}
              >
                {adminMode ? <EyeOff size={14} className="text-rose-500" /> : <Eye size={14} className="text-indigo-50" />}
                <span className="text-[8px] font-black uppercase text-slate-600 tracking-widest">{adminMode ? 'Staff view' : 'User view'}</span>
              </button>
            )}
          </div>

          <div className="flex items-center gap-2.5 sm:gap-3.5 md:gap-4 shrink-0">
             {/* Network Connectivity Status Indicator */}
             <NetworkStatusIndicator />

             {/* Theme Toggle Button (Light/Dark Mode) */}
             <button
               id="theme-mode-toggle-btn"
               onClick={() => setTheme(prev => prev === 'light' ? 'dark' : 'light')}
               className={`min-w-[36px] min-h-[36px] w-9 h-9 sm:w-10 sm:h-10 md:w-11 md:h-11 flex items-center justify-center rounded-xl md:rounded-2xl transition-all active:scale-95 shadow-sm shrink-0 border relative group cursor-pointer ${
                 theme === 'light'
                   ? 'border-amber-300 bg-gradient-to-br from-amber-50 to-amber-100/90 hover:from-amber-100 hover:to-amber-200/90 text-amber-600 shadow-[0_2px_10px_rgba(245,158,11,0.25)] ring-2 ring-amber-400/40 hover:ring-amber-400/70'
                   : 'border-indigo-700/80 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 hover:from-slate-850 hover:to-indigo-900 text-indigo-300 shadow-[0_2px_10px_rgba(99,102,241,0.3)] ring-2 ring-indigo-500/40 hover:ring-indigo-500/70'
               }`}
               title={theme === 'light' ? 'Light Mode Active — Click to switch to Dark Mode' : 'Dark Mode Active — Click to switch to Light Mode'}
               aria-label={`Switch theme (Currently ${theme} mode)`}
             >
               {theme === 'light' ? (
                 <Sun 
                   size={20} 
                   className="text-amber-500 fill-amber-400/40 drop-shadow-sm transition-transform duration-300 group-hover:rotate-45 group-hover:scale-110 shrink-0" 
                 />
               ) : (
                 <Moon 
                   size={20} 
                   className="text-indigo-300 fill-indigo-400/30 drop-shadow-sm transition-transform duration-300 group-hover:-rotate-12 group-hover:scale-110 shrink-0" 
                 />
               )}
               <span className="sr-only">{theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}</span>
             </button>

             {isAuthenticated && !isAdmin && <NotificationCenter />}
             
             {isAuthenticated ? (
             <div className="relative" ref={menuRef}>
                <button 
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                  className={`flex items-center gap-2 md:gap-3 p-1 pr-1 md:pr-4 rounded-2xl md:rounded-[1.4rem] transition-all active:scale-95 group shadow-sm ${
                    tokens.isClinical
                      ? 'bg-slate-800/60 hover:bg-slate-800 border border-slate-700 text-white'
                      : tokens.isHighContrast
                      ? 'bg-amber-300 hover:bg-amber-400 border-2 border-black text-black'
                      : tokens.isMinimal
                      ? 'bg-slate-900 hover:bg-slate-800 border border-slate-800 text-white'
                      : tokens.isVibrant
                      ? 'bg-slate-900/60 hover:bg-slate-800 border border-violet-800/60 text-white'
                      : 'bg-slate-50/50 dark:bg-slate-900/50 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100'
                  }`}
                  aria-label="Toggle user profile menu"
                  aria-haspopup="true"
                  aria-expanded={profileMenuOpen}
                >
                  <div className="relative">
                    {isElite && (
                        <div className="absolute inset-[-3px] rounded-xl bg-gradient-to-tr from-amber-400 via-yellow-200 to-amber-600 animate-spin" style={{ padding: '2px', animationDuration: '4s' }}>
                            <div className="w-full h-full bg-white rounded-xl"></div>
                        </div>
                    )}
                    <div className={`relative w-8 h-8 md:w-11 md:h-11 rounded-xl flex items-center justify-center font-black text-white text-xs md:text-base shadow-md transition-transform group-hover:scale-105 z-10 ${isElite ? 'bg-slate-900' : isAdmin ? 'bg-rose-500' : tokens.iconBg}`}>
                        {user?.name?.charAt(0).toUpperCase()}
                    </div>
                  </div>
                  <div className={`hidden ${adConfig.headerSlimMobile ? 'sm:block' : 'block'} text-left`}>
                    <p className={`text-[10px] md:text-xs font-black uppercase tracking-tight truncate max-w-[80px] ${
                      tokens.isClinical ? 'text-white' : tokens.isHighContrast ? 'text-black' : 'text-slate-900 dark:text-slate-100'
                    }`}>{user?.name?.split(' ')[0]}</p>
                    <p className={`text-[7px] md:text-[8px] font-black uppercase tracking-widest ${
                      tokens.isClinical ? 'text-slate-300' : tokens.isHighContrast ? 'text-black/80' : 'text-slate-400 dark:text-slate-400'
                    }`}>{isAdmin ? 'ADMIN' : isElite ? 'PRO TIER' : 'FREE TIER'}</p>
                  </div>
                  <ChevronDown size={14} className={`transition-transform ${
                    tokens.isClinical ? 'text-slate-300' : tokens.isHighContrast ? 'text-black' : 'text-slate-400 dark:text-slate-400'
                  } ${profileMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {profileMenuOpen && (
                    <div 
                      className={`absolute right-0 mt-3 w-64 md:w-72 rounded-[2rem] shadow-[0_20px_60px_rgba(0,0,0,0.15)] border overflow-hidden animate-pop z-[110] ${
                        tokens.isHighContrast 
                          ? 'bg-white border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]' 
                          : tokens.isClinical
                          ? 'bg-white border-teal-200 dark:bg-slate-900 dark:border-slate-800'
                          : tokens.isVibrant
                          ? 'bg-white border-violet-200 dark:bg-slate-900 dark:border-slate-800'
                          : 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800'
                      }`}
                      role="menu"
                      aria-label="User profile options"
                    >
                        <div className={`p-6 border-b ${
                          tokens.isClinical ? 'bg-teal-50/50 dark:bg-slate-950/50 border-teal-100 dark:border-slate-800' :
                          tokens.isHighContrast ? 'bg-amber-300 border-b-2 border-black' :
                          tokens.isVibrant ? 'bg-violet-50/50 dark:bg-slate-950/50 border-violet-100 dark:border-slate-800' :
                          'bg-slate-50 dark:bg-slate-950/50 border-slate-100 dark:border-slate-800'
                        }`}>
                             <div className="flex items-center gap-4">
                                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black text-lg text-white shadow-lg ${
                                  isElite ? 'bg-gradient-to-br from-amber-400 to-amber-600' : 
                                  isAdmin ? 'bg-rose-500' : 
                                  tokens.iconBg
                                }`}>
                                    {user?.name?.charAt(0).toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                    <p className={`font-black text-sm uppercase truncate ${tokens.isHighContrast ? 'text-black' : 'text-slate-900 dark:text-white'}`}>{user?.name}</p>
                                    <p className={`text-[9px] font-bold uppercase tracking-widest truncate ${tokens.isHighContrast ? 'text-black/70' : 'text-slate-400'}`}>{user?.email}</p>
                                </div>
                             </div>
                        </div>
                        <div className="p-2 space-y-1">
                            <button onClick={() => handleNavigate('profile')} className={`w-full flex items-center gap-4 px-5 py-4 rounded-xl transition-all font-black text-[10px] uppercase tracking-widest ${
                              tokens.isClinical ? 'text-slate-700 dark:text-slate-200 hover:bg-teal-50 dark:hover:bg-teal-950/40 hover:text-[#0F7D6C]' :
                              tokens.isHighContrast ? 'text-black hover:bg-amber-300 font-black' :
                              tokens.isVibrant ? 'text-slate-700 dark:text-slate-200 hover:bg-violet-50 dark:hover:bg-violet-950/40 hover:text-violet-700' :
                              'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-indigo-600'
                            }`}>
                                <UserCircle size={18} /> My Study Hub Profile
                            </button>
                            <button onClick={() => handleNavigate('help')} className={`w-full flex items-center gap-4 px-5 py-4 rounded-xl transition-all font-black text-[10px] uppercase tracking-widest ${
                              tokens.isClinical ? 'text-slate-700 dark:text-slate-200 hover:bg-teal-50 dark:hover:bg-teal-950/40 hover:text-[#0F7D6C]' :
                              tokens.isHighContrast ? 'text-black hover:bg-amber-300 font-black' :
                              tokens.isVibrant ? 'text-slate-700 dark:text-slate-200 hover:bg-violet-50 dark:hover:bg-violet-950/40 hover:text-violet-700' :
                              'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-indigo-600'
                            }`}>
                                <HelpCircle size={18} /> Study Hub Manual
                            </button>
                            <button 
                              onClick={() => setTheme(prev => prev === 'light' ? 'dark' : 'light')} 
                              className={`w-full flex items-center justify-between px-5 py-3.5 rounded-xl transition-all font-black text-[10px] uppercase tracking-widest cursor-pointer ${
                                tokens.isClinical ? 'text-slate-700 dark:text-slate-200 hover:bg-teal-50 dark:hover:bg-teal-950/40' :
                                tokens.isHighContrast ? 'text-black hover:bg-amber-300 font-black' :
                                tokens.isVibrant ? 'text-slate-700 dark:text-slate-200 hover:bg-violet-50 dark:hover:bg-violet-950/40' :
                                'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                              }`}
                            >
                                <div className="flex items-center gap-3">
                                  {theme === 'light' ? (
                                    <Sun size={18} className="text-amber-500 fill-amber-400/40" />
                                  ) : (
                                    <Moon size={18} className="text-indigo-400 fill-indigo-400/30" />
                                  )}
                                  <span>Theme: {theme === 'light' ? 'Light Mode' : 'Dark Mode'}</span>
                                </div>
                                <span className={`px-2.5 py-1 rounded-lg text-[8px] font-black uppercase tracking-widest ${
                                  theme === 'light' 
                                    ? 'bg-amber-100 text-amber-800 border border-amber-300/80 shadow-xs' 
                                    : 'bg-indigo-950 text-indigo-300 border border-indigo-700/80 shadow-xs'
                                }`}>
                                  Toggle
                                </span>
                            </button>
                            <div className="h-px bg-slate-100 dark:bg-slate-800 my-2 mx-4"></div>
                            <button onClick={logout} className="w-full flex items-center gap-4 px-5 py-4 rounded-xl text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-all font-black text-[10px] uppercase tracking-widest">
                                <LogOut size={18} /> Log Out
                            </button>
                        </div>
                        {isElite && (
                            <div className="p-5 bg-gradient-to-r from-amber-50 to-amber-100/50 dark:from-amber-950/30 dark:to-amber-900/20 flex items-center gap-4">
                                <ShieldCheck size={20} className="text-amber-600" />
                                <div className="min-w-0">
                                    <p className="text-[9px] font-black text-amber-700 dark:text-amber-400 uppercase tracking-widest">Security Verified</p>
                                    <p className="text-[7px] font-bold text-amber-600/70 dark:text-amber-500/70 uppercase">Elite Protocol Active</p>
                                </div>
                            </div>
                        )}
                    </div>
                )}
              </div>
              ) : (
                <button
                  onClick={() => window.location.href = 'https://www.diamondnursingschool.com/studyhub-login'}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white font-black text-[10px] md:text-xs uppercase tracking-wider bg-[#0F7D6C] hover:bg-[#0B6356] transition-all shadow-md active:scale-95 cursor-pointer"
                >
                  Portal Sign-In
                </button>
              )}
          </div>
        </header>

        <div className={`flex-1 flex flex-col ${mainWrapperClass}`}>
            <main className="flex-1 flex flex-col px-4 py-3 sm:p-4 md:p-6 lg:p-8 transform-gpu relative">
            <div className="max-w-7xl mx-auto w-full flex-1">
                <Breadcrumbs />
                <Routes>
                    <Route path="/manage/*" element={<Routes>
                        <Route index element={<Navigate to="admin-dashboard" replace />} />
                        <Route path="admin-dashboard" element={<PermissionGuard permission="admin.dashboard"><AdminDashboard /></PermissionGuard>} />
                        <Route path="admin-analytics" element={<PermissionGuard permission="admin.dashboard"><SuperAdminAnalytics /></PermissionGuard>} />
                        <Route path="admin-journey-auditor" element={<PermissionGuard permission="admin.dashboard"><UserJourneyAuditor /></PermissionGuard>} />
                        <Route path="admin-cert-auditor" element={<PermissionGuard permission="admin.dashboard"><CertAuditor /></PermissionGuard>} />
                        <Route path="admin-faculty" element={<PermissionGuard permission="admin.dashboard"><FacultyManager /></PermissionGuard>} />
                        <Route path="admin-certifications" element={<PermissionGuard permission="admin.certifications"><CertificationManager /></PermissionGuard>} />
                        <Route path="admin-factory" element={<PermissionGuard permission="admin.factory"><AdminAIFactory /></PermissionGuard>} />
                        <Route path="admin-prompts" element={<PermissionGuard permission="admin.prompts"><PromptConsole /></PermissionGuard>} />
                        <Route path="admin-questions" element={<PermissionGuard permission="admin.questions"><QuestionManager /></PermissionGuard>} />
                        <Route path="admin-flashcards" element={<PermissionGuard permission="admin.flashcards"><FlashcardManager /></PermissionGuard>} />
                        <Route path="admin-users" element={<PermissionGuard permission="admin.users"><UserManager /></PermissionGuard>} />
                        <Route path="admin-2fa" element={<Navigate to="/manage/admin-users" replace />} />
                        <Route path="admin-config" element={<PermissionGuard permission="admin.config"><ExamConfigManager /></PermissionGuard>} />
                        <Route path="admin-sponsored" element={<PermissionGuard permission="admin.sponsored"><AdManager /></PermissionGuard>} />
                        <Route path="admin-promos" element={<PermissionGuard permission="admin.promos"><PromoManager /></PermissionGuard>} />
                        <Route path="admin-study-content" element={<PermissionGuard permission="admin.study-content"><StudyContentManager /></PermissionGuard>} />
                        <Route path="admin-skills-content" element={<PermissionGuard permission="admin.skills-content"><SkillsContentManager /></PermissionGuard>} />
                        <Route path="admin-clinical-simulators" element={<PermissionGuard permission="admin.skills-content"><ClinicalSimulatorStudio /></PermissionGuard>} />
                        <Route path="admin-simulator" element={<PermissionGuard permission="admin.simulator-lab"><SimulatorManager /></PermissionGuard>} />
                        <Route path="admin-podcasts" element={<PermissionGuard permission="admin.podcasts"><PodcastManager /></PermissionGuard>} />
                        <Route path="admin-ai-status" element={<PermissionGuard permission="admin.ai-status"><AiStatusManager /></PermissionGuard>} />
                        <Route path="admin-notifications" element={<PermissionGuard permission="admin.dashboard"><NotificationManager /></PermissionGuard>} />
                        <Route path="admin-database" element={<PermissionGuard permission="admin.database"><DatabaseManager /></PermissionGuard>} />
                        <Route path="admin-seo" element={<PermissionGuard permission="admin.config"><SeoManager /></PermissionGuard>} />
                        <Route path="admin-faqs" element={<PermissionGuard permission="admin.config"><FaqManager /></PermissionGuard>} />
                        <Route path="admin-scenarios" element={<PermissionGuard permission="admin.scenarios"><ScenarioManager /></PermissionGuard>} />
                        <Route path="admin-feedback" element={<PermissionGuard permission="admin.feedback"><FeedbackManager /></PermissionGuard>} />
                    </Routes>} />
                    
                    <Route path="/cookie-consent" element={<CookieConsentPage />} />
                    <Route path="/cookies" element={<CookieConsentPage />} />
                    <Route path="/compliance/cookies" element={<CookieConsentPage />} />
                    <Route path="/compliance/cookie-policy" element={<CookieConsentPage />} />
                    <Route path="/practice-exams/cookie-consent" element={<CookieConsentPage />} />
                    <Route path="/practice-exams/cookies" element={<CookieConsentPage />} />

                    <Route path="/faq" element={<FaqPage />} />
                    <Route path="/faq/:certType" element={<FaqPage />} />
                    
                    <Route path="/program" element={<ProgramDetailsPage />} />
                    <Route path="/program/:certId" element={<ProgramDetailsPage />} />
                    <Route path="/program/:certId/*" element={<ProgramDetailsPage />} />
                    <Route path="/programs" element={<ProgramDetailsPage />} />
                    <Route path="/programs/:certId" element={<ProgramDetailsPage />} />
                    <Route path="/programs/:certId/*" element={<ProgramDetailsPage />} />
                    <Route path="/certifications" element={<ProgramDetailsPage />} />
                    <Route path="/certifications/:certId" element={<ProgramDetailsPage />} />
                    <Route path="/certifications/:certId/*" element={<ProgramDetailsPage />} />
                    
                    <Route path="/practice-exams/program" element={<ProgramDetailsPage />} />
                    <Route path="/practice-exams/program/:certId" element={<ProgramDetailsPage />} />
                    <Route path="/practice-exams/program/:certId/*" element={<ProgramDetailsPage />} />
                    <Route path="/practice-exams/programs" element={<ProgramDetailsPage />} />
                    <Route path="/practice-exams/programs/:certId" element={<ProgramDetailsPage />} />
                    <Route path="/practice-exams/programs/:certId/*" element={<ProgramDetailsPage />} />
                    <Route path="/practice-exams/certifications" element={<ProgramDetailsPage />} />
                    <Route path="/practice-exams/certifications/:certId" element={<ProgramDetailsPage />} />
                    <Route path="/practice-exams/certifications/:certId/*" element={<ProgramDetailsPage />} />
                    <Route path="/practice-exams/faq" element={<FaqPage />} />
                    <Route path="/practice-exams/faq/:certType" element={<FaqPage />} />
                    <Route path="/practice-exams/:certId/*" element={<RedirectToPractice />} />
                    <Route path="/practice-exams" element={<NavigateWithCert target="dashboard" />} />
                    <Route path="/practice" element={<NavigateWithCert target="dashboard" />} />
                    
                    <Route path="/admin-clinical-simulators" element={<AdminDirectRedirect defaultSubpage="clinical-simulators" />} />
                    <Route path="/admin-:subpage" element={<AdminDirectRedirect />} />

                    <Route path="/404" element={<NotFoundPage />} />
                    
                    <Route path="/gateway" element={<GatewayRedirect />} />
                    <Route path="/gateway/*" element={<GatewayRedirect />} />

                    <Route path="/study-quiz" element={<NavigateWithCert target="practice" />} />
                    
                    <Route path="/" element={<GatewayRedirect />} />
                    
                    {/* Primary Unified Certification Practice Routes: /practice/:certId/* */}
                    <Route path="/practice/:certId/*" element={<>
                    <CertSyncHandler />
                    <Routes>
                        <Route path="dashboard" element={<Dashboard onNavigate={handleNavigate} />} />
                        <Route path="faculty" element={<Faculty />} />
                        <Route path="analytics" element={<AdvancedAnalytics />} />
                        <Route path="guide" element={<StudyGuide />} />
                        <Route path="flashcards" element={<Flashcards />} />
                        <Route path="skills" element={<SkillsLab />} />
                        <Route path="simulator" element={<SimulatorLab />} />
                        <Route path="podcasts" element={<Podcasts />} />
                        <Route path="exam" element={<PracticeExam initialMode="standard" />} />
                        <Route path="history" element={<ExamHistory />} />
                        <Route path="tutor" element={<AITutor />} />
                        <Route path="study" element={<Navigate to="../practice" replace />} />
                        <Route path="missed" element={<StudyQuiz defaultTopic="Missed Questions" />} />
                        <Route path="scenario" element={<ScenarioPicker />} />
                        <Route path="scenario/:challengeId" element={<PracticeExam initialMode="scenario_quiz" />} />
                        <Route path="search" element={<RegistrySearch />} />
                        <Route path="profile" element={<Profile />} />
                        <Route path="elite" element={<EliteUpgrade />} />
                        <Route path="help" element={<HelpPage />} />
                        <Route path="program" element={<ProgramDetailsPage />} />
                        <Route path="programs" element={<ProgramDetailsPage />} />
                        <Route path="overview" element={<ProgramDetailsPage />} />
                        <Route path="details" element={<ProgramDetailsPage />} />
                        <Route path="practice" element={<StudyQuiz />} />
                        <Route path="study-quiz" element={<StudyQuiz />} />
                        <Route path="cookie-consent" element={<CookieConsentPage />} />
                        <Route path="cookies" element={<CookieConsentPage />} />
                        <Route path="*" element={<Navigate to="dashboard" replace />} />
                    </Routes>
                    </>} />

                    {/* Backward Compatibility Redirects: /:certId/* -> /practice/:certId/* */}
                    <Route path="/:certId/*" element={<RedirectToPractice />} />

                    <Route path="/cnaprep/*" element={<Navigate to="/practice/cna/dashboard" replace />} />
                    <Route path="*" element={<Navigate to="/404" replace />} />
                </Routes>
            </div>
            </main>
            {adConfig.isFooterEnabled && <Footer config={adConfig} />}
            <PromoBanner />
            <GlobalAudioPlayer />
            <MiniPlayer />

            {showExitSurvey && (
              <div id="global-exit-intent-survey" className="fixed inset-0 z-[99999] bg-slate-950/70 backdrop-blur-md flex items-center justify-center p-3 md:p-4 overflow-y-auto animate-fade-in" aria-modal="true" role="dialog">
                <div className="relative w-full max-w-lg bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 shadow-2xl p-6 md:p-8 space-y-5 text-left my-auto animate-scale-up max-h-[92vh] md:max-h-[90vh] overflow-y-auto flex flex-col scrollbar-thin">
                  
                  {/* Experiment active swap indicator */}
                  <div className="flex items-center justify-between border-b border-dashed border-slate-100 dark:border-slate-850 pb-3 shrink-0">
                    <span className="flex items-center gap-1.5 px-2.5 py-1 bg-indigo-50 dark:bg-indigo-950/45 border border-indigo-100 dark:border-indigo-900/50 rounded-full text-[9px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">
                      <Sparkles size={10} className="animate-pulse" />
                      {isSurveyUserNewOrNonActive ? "Exit Intent Feedback" : "Active A/B Test Variant"}
                    </span>
                    {!isSurveyUserNewOrNonActive && (
                      <button
                        onClick={() => {
                          setSurveyFormat(prev => prev === 'struggle' ? 'wishlist' : 'struggle');
                          setSelectedStruggleReason('');
                        }}
                        className="px-3 py-1 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-750 text-slate-550 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 rounded-full text-[9px] font-bold uppercase border border-slate-150 dark:border-slate-700 transition-all select-none hover:border-slate-300"
                      >
                        Swap to {surveyFormat === 'struggle' ? 'Wishlist Format' : 'Struggle Format'}
                      </button>
                    )}
                  </div>

                  <div className="flex items-start justify-between shrink-0">
                     <div className="flex items-center gap-3">
                       {surveyFormat === 'struggle' ? (
                         <>
                           <div className="p-3 bg-rose-50 dark:bg-rose-950/40 border border-rose-100 dark:border-rose-900/50 text-rose-500 dark:text-rose-400 rounded-2xl shrink-0">
                             <AlertCircle size={24} className="animate-pulse" />
                           </div>
                           <div>
                             <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">Is something hard to understand?</h3>
                             <p className="text-[10px] uppercase font-black text-slate-400 dark:text-slate-500 tracking-widest mt-0.5">Automated Student Struggle Survey</p>
                           </div>
                         </>
                       ) : (
                         <>
                           <div className="p-3 bg-purple-50 dark:bg-purple-950/40 border border-purple-100 dark:border-purple-900/50 text-purple-600 dark:text-purple-400 rounded-2xl shrink-0">
                             <Sparkles size={24} className="animate-bounce" />
                           </div>
                           <div>
                             <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">What should we build next?</h3>
                             <p className="text-[10px] uppercase font-black text-purple-400 dark:text-purple-500 tracking-widest mt-0.5">Voter Wishlist</p>
                           </div>
                         </>
                       )}
                     </div>
                     <button 
                       onClick={handleCancelExitSurvey}
                       className="p-2 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 rounded-xl transition-all border border-slate-100 dark:border-slate-750 active:scale-95 flex items-center justify-center shrink-0"
                       aria-label="Close dialog"
                     >
                       <X size={16} />
                     </button>
                  </div>

                  {surveySubmittedSuccessfully ? (
                     <div className="py-12 text-center space-y-4 animate-fade-in flex-1 flex flex-col justify-center items-center">
                       <div className="w-16 h-16 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-105 dark:border-emerald-900/50 text-emerald-500 dark:text-emerald-400 rounded-full flex items-center justify-center shadow-sm">
                         <CheckCircle2 size={32} />
                       </div>
                       <div className="space-y-4 mt-4">
                         <h4 className="text-lg font-black text-slate-900 dark:text-white leading-none">Feedback Received!</h4>
                         <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold max-w-xs mx-auto leading-relaxed">
                           {surveyFormat === 'struggle' 
                             ? "Thank you! We use your feedback to make our study guides and quizzes simpler and easier to understand."
                             : "Got it! Your vote has been saved. We will start working on the top features first."
                           }
                         </p>
                       </div>
                     </div>
                  ) : (
                     <div className="space-y-4 flex-1">
                       <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold leading-relaxed">
                         {surveyFormat === 'struggle'
                           ? "We want to make this app as simple as possible for you. Tell us what is hard or confusing so we can fix it right away!"
                           : "Vote on the single best feature you would love to see next in the app!"
                         }
                       </p>

                       <div className="space-y-2">
                         <label className="text-[10px] uppercase font-black text-slate-400 dark:text-slate-500 tracking-wider font-extrabold block">
                           {surveyFormat === 'struggle' ? "Select what you are finding hard:" : "Select your highly-desired feature:"}
                         </label>
                         
                         {surveyFormat === 'struggle' ? (
                           <div className="space-y-2">
                             {[
                               { id: 'difficult_content', label: 'Terminology or slides are too complex/overwhelming' },
                               { id: 'confusing_ui', label: 'Navigation or interface controls are confusing' },
                               { id: 'missing_info', label: 'I could not find the exact topic or question of interest' },
                               { id: 'leaving_now', label: 'Just browsing, but plan to return to study later' },
                               { id: 'other', label: 'Other custom struggle or obstacle' }
                             ].map(reason => (
                               <button
                                 key={reason.id}
                                 onClick={() => setSelectedStruggleReason(reason.id)}
                                 className={`w-full p-3 rounded-2xl border text-left text-xs font-bold transition-all flex items-center justify-between group active:scale-[0.99] ${
                                   selectedStruggleReason === reason.id
                                     ? 'bg-rose-50 dark:bg-rose-950/20 border-rose-200 dark:border-rose-900/60 text-rose-900 dark:text-rose-100 shadow-sm'
                                     : 'bg-slate-50 dark:bg-slate-800/40 border-slate-105 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-750'
                                 }`}
                               >
                                 <span>{reason.label}</span>
                                 <span className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ml-3 transition-all ${
                                   selectedStruggleReason === reason.id
                                     ? 'border-rose-500 bg-rose-500'
                                     : 'border-slate-205 dark:border-slate-700 bg-white dark:bg-slate-800 group-hover:border-slate-300'
                                 }`}>
                                   {selectedStruggleReason === reason.id && (
                                     <span className="w-1.5 h-1.5 rounded-full bg-white dark:bg-slate-900 animate-scale-up" />
                                   )}
                                 </span>
                               </button>
                             ))}
                           </div>
                         ) : (
                           <div className="space-y-2">
                             {[
                               { id: 'flashcards', label: 'Pre-made flashcards & spaced repetition games' },
                               { id: 'study_groups', label: 'Real-time student chat & peer study groups' },
                               { id: 'video_skills', label: 'Professional video walkthroughs of nursing skills' },
                               { id: 'custom_mock', label: 'Unlimited custom-built mock examinations' },
                               { id: 'mobile_app', label: 'Downloadable mobile app with offline notifications' }
                             ].map(feature => (
                               <button
                                 key={feature.id}
                                 onClick={() => setSelectedStruggleReason(feature.id)}
                                 className={`w-full p-3 rounded-2xl border text-left text-xs font-bold transition-all flex items-center justify-between group active:scale-[0.99] ${
                                   selectedStruggleReason === feature.id
                                     ? 'bg-purple-50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-900/60 text-purple-900 dark:text-purple-100 shadow-sm'
                                     : 'bg-slate-50 dark:bg-slate-800/40 border-slate-105 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-750'
                                 }`}
                               >
                                 <span>{feature.label}</span>
                                 <span className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ml-3 transition-all ${
                                   selectedStruggleReason === feature.id
                                     ? 'border-purple-500 bg-purple-500'
                                     : 'border-slate-205 dark:border-slate-700 bg-white dark:bg-slate-800 group-hover:border-slate-300'
                                 }`}>
                                   {selectedStruggleReason === feature.id && (
                                     <span className="w-1.5 h-1.5 rounded-full bg-white dark:bg-slate-900 animate-scale-up" />
                                   )}
                                 </span>
                               </button>
                             ))}
                           </div>
                         )}
                       </div>

                       <div className="space-y-1.5">
                         <label className="text-[10px] uppercase font-black text-slate-400 dark:text-slate-500 tracking-wider font-bold block">
                           {surveyFormat === 'struggle' ? "Detail your struggle (Optional):" : "Additional features or feedback (Optional):"}
                         </label>
                         <textarea
                           value={customStruggleMessage}
                           onChange={(e) => setCustomStruggleMessage(e.target.value)}
                           placeholder={surveyFormat === 'struggle' 
                             ? "Tell us what words or parts are hard to understand..."
                             : "Tell us about other learning tools you would like..."
                           }
                           className="w-full h-20 p-3 rounded-2xl text-xs font-semibold text-slate-800 dark:text-slate-100 bg-slate-50 dark:bg-slate-850/65 border border-slate-105 dark:border-slate-805 focus:outline-none focus:ring-2 focus:ring-rose-500/10 focus:border-rose-300 resize-none placeholder-slate-400 dark:placeholder-slate-500"
                         />
                       </div>

                       {surveyFormat === 'struggle' ? (
                         <div className="p-3.5 bg-indigo-55/40 dark:bg-indigo-950/20 border border-indigo-100/50 dark:border-indigo-900/40 rounded-2xl flex items-start gap-3">
                           <Sparkles className="text-indigo-600 dark:text-indigo-400 shrink-0 mt-0.5 animate-pulse" size={16} />
                           <div>
                             <h5 className="text-[11px] font-black text-indigo-950 dark:text-indigo-200">Did you know?</h5>
                             <p className="text-[10px] text-indigo-800 dark:text-indigo-300 font-medium leading-relaxed mt-0.5">
                               You can ask our friendly <strong>AI Tutor</strong> for quick summaries or memory hacks on any slide!
                             </p>
                           </div>
                         </div>
                       ) : (
                         <div className="p-3.5 bg-emerald-50/40 dark:bg-emerald-950/20 border border-emerald-100/50 dark:border-emerald-900/40 rounded-2xl flex items-start gap-3">
                           <Sparkles className="text-emerald-500 dark:text-emerald-450 shrink-0 mt-0.5 animate-pulse" size={16} />
                           <div>
                             <h5 className="text-[11px] font-black text-emerald-950 dark:text-emerald-200">Elite Advantage</h5>
                             <p className="text-[10px] text-emerald-800 dark:text-emerald-300 font-medium leading-relaxed mt-0.5 px-0 pb-0">
                               Wishlist items are fast-tracked for upgraded <strong>Elite Pro</strong> students!
                             </p>
                           </div>
                         </div>
                       )}

                        {surveySubmitError && (
                          <div id="survey-submit-error" className="w-full p-3 bg-rose-50 dark:bg-rose-950/25 border border-rose-100 dark:border-rose-900/45 rounded-2xl flex items-center gap-2.5 text-rose-800 dark:text-rose-300 text-[11px] font-bold animate-shake mb-4 shrink-0">
                            <AlertCircle size={14} className="shrink-0 text-rose-500 animate-pulse" />
                            <span>{surveySubmitError}</span>
                          </div>
                        )}

                       <div className="flex items-center gap-3 pt-2">
                         <button
                           onClick={handleCancelExitSurvey}
                           className="flex-1 py-3 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 font-black text-[10px] tracking-widest uppercase rounded-2xl transition-all active:scale-95 text-center leading-none"
                           disabled={isSubmittingSurvey}
                         >
                           Resume Study
                         </button>
                         <button
                           onClick={handleSubmitExitSurvey}
                           className="flex-1 py-3 bg-slate-900 dark:bg-slate-105 hover:bg-slate-800 dark:hover:bg-white disabled:opacity-50 text-white dark:text-slate-900 font-black text-[10px] tracking-widest uppercase rounded-2xl transition-all active:scale-95 shadow-md flex items-center justify-center gap-2 leading-none"
                           disabled={isSubmittingSurvey || !selectedStruggleReason}
                         >
                           {isSubmittingSurvey ? (
                             <Loader2 size={12} className="animate-spin" />
                           ) : (
                             <span>Submit Selection</span>
                           )}
                         </button>
                       </div>
                     </div>
                  )}
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

const HashUrlNormalizer: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const cleanSearch = cleanSearchParams(window.location.search);

      // 1. If user arrives via a legacy hash link (e.g., /#/programs/cna or /practice-exams/#/cna/dashboard)
      if (window.location.hash && window.location.hash.length > 1) {
        const rawHash = window.location.hash.replace(/^#\/?/, '').replace(/^\/+/, '');
        if (rawHash && !rawHash.startsWith('!') && !rawHash.startsWith('#')) {
          const cleanTarget = '/' + rawHash;
          window.history.replaceState(null, '', cleanTarget + cleanSearch);
          navigate(cleanTarget + cleanSearch, { replace: true });
          return;
        }
      }

      // 2. If user arrives via /practice-exams/... path prefix
      const pathname = window.location.pathname;
      if (pathname.startsWith('/practice-exams/')) {
        const rest = pathname.replace(/^\/practice-exams/, '');
        const cleanPath = (rest.startsWith('/programs') || rest.startsWith('/program') || rest.startsWith('/certifications') || rest.startsWith('/faq'))
          ? rest
          : `/practice${rest}`;
        window.history.replaceState(null, '', cleanPath + cleanSearch);
        navigate(cleanPath + cleanSearch, { replace: true });
      }
    }
  }, [navigate]);

  return null;
};

const App: React.FC = () => (
  <Router>
    <HashUrlNormalizer />
    <AuthProvider>
      <AudioProvider>
        <Routes>
          <Route path="*" element={
            <AuthGuard>
              <AppLayout />
            </AuthGuard>
          } />
        </Routes>
      </AudioProvider>
    </AuthProvider>
  </Router>
);

export default App;
